import { useState } from 'react';
import { useSelectMtoOfferMutation } from '@shared/api';
import type { ComparisonRow } from '@entities/mto';
import {
  apiErrorMessage,
  fmtDateTime,
  fmtPercent,
  fmtPercentDelta,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { offerPrice } from '../helpers';
import {
  CurrentSelection,
  DetailCell,
  ExpandButton,
  Muted,
  OfferList,
  ReasonInput,
  SelectionBar,
} from '../styles';
import { MinPrice, ExpiredNote } from './MinPrice';
import { OfferRow } from './OfferRow';

export function ComparisonRowView({
  row,
  runId,
  canWrite,
  onError,
}: {
  row: ComparisonRow;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [select, { isLoading }] = useSelectMtoOfferMutation();

  const selection = row.selection;
  const unitLabel = row.nomenclature_unit ? `, ${row.nomenclature_unit}` : '';

  async function handleSelect() {
    if (!picked) return;
    onError('');
    try {
      await select({ id: picked, runId, reason: reason.trim() || null }).unwrap();
      setPicked(null);
      setReason('');
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось зафиксировать выбор поставщика.');
      // eslint-disable-next-line no-console
      console.error('selectMtoOffer failed', err);
    }
  }

  return (
    <>
      <tr>
        <td>
          <ExpandButton
            type="button"
            $open={open}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            {row.nomenclature_name}
            {unitLabel}
          </ExpandButton>
        </td>
        <td className="num">{row.offers.length}</td>
        <td className="num">
          <MinPrice value={row.min_price_value} currency={row.compare_currency} offers={row.offers} />
        </td>
        <td className="num">{fmtPercent(row.spread_percent)}</td>
        <td className="num">{fmtPercentDelta(row.trend_percent)}</td>
        <td>
          {selection ? (
            <Badge $tone="success">{selection.supplier_name ?? 'выбран'}</Badge>
          ) : (
            <Badge $tone="neutral">не выбран</Badge>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <DetailCell colSpan={6}>
            <OfferList>
              {row.offers.map((o) => (
                <OfferRow
                  key={o.offer_id}
                  offer={o}
                  group={row.nomenclature_id}
                  compareCurrency={row.compare_currency}
                  selectable={canWrite && !isLoading}
                  checked={picked === o.offer_id}
                  onPick={setPicked}
                />
              ))}
            </OfferList>

            {row.incomparable_count > 0 && (
              <Muted>
                Не участвует в сравнении: {row.incomparable_count}. Цены сравниваются внутри одной
                единицы и валюты — курса в системе нет, пересчёт не делаем.
              </Muted>
            )}
            {row.offers.some((o) => o.expired) && <ExpiredNote />}

            {canWrite && (
              <SelectionBar>
                <ReasonInput
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Основание: почему этот поставщик (срок, отсрочка, качество)"
                  aria-label="Основание выбора поставщика"
                />
                <Button
                  type="button"
                  $variant="primary"
                  onClick={handleSelect}
                  disabled={!picked || isLoading}
                  title={picked ? undefined : 'Отметьте поставщика в списке выше'}
                >
                  {isLoading ? 'Сохраняем…' : 'Зафиксировать выбор'}
                </Button>
              </SelectionBar>
            )}

            {selection && (
              <CurrentSelection>
                Выбран {selection.supplier_name ?? '— поставщик не указан —'} по цене{' '}
                {offerPrice(selection)} · {fmtDateTime(selection.selected_at)}
                {selection.reason ? ` · основание: ${selection.reason}` : ''}
              </CurrentSelection>
            )}
          </DetailCell>
        </tr>
      )}
    </>
  );
}
